import { Button } from '@/components/shadcn/button'
import { TableCell, TableRow } from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { IconCirclePlusOutline } from '@icons/IconCirclePlusOutline'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconHelpQuestionOutline } from '@icons/IconHelpQuestionOutline'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { TooltipWrapper } from '@/components/ui/TooltipWrapper'
import { MenuItemForm } from './MenuItemForm'
import clsx from 'clsx'
import { MessagesLandingMenuItem } from './types'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useState } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { IconDragHandleOutline } from '@icons/IconDragHandleOutline'

type DropPosition = 'before' | 'after' | 'inside'

interface MenuItemProps {
  item: MessagesLandingMenuItem
  id: string
  depth: number
  isExpanded: boolean
  hasChildren: boolean
  showIcon: boolean
  toggleItem: (id: string) => void
  addChildItem: (id: string, newItem: MessagesLandingMenuItem) => void
  deleteItem: (id: string) => void
  editItem: (id: string, updatedItem: Partial<MessagesLandingMenuItem>) => void
  activeDropPosition?: DropPosition
}

export function MenuItem({
  item,
  id,
  depth,
  isExpanded,
  hasChildren,
  showIcon,
  toggleItem,
  addChildItem,
  deleteItem,
  editItem,
  activeDropPosition,
}: MenuItemProps) {
  const paddingLeft = depth * 20
  const [isAddChildItemSheetOpen, setIsAddChildItemSheetOpen] = useState(false)
  const [isEditItemSheetOpen, setIsEditItemSheetOpen] = useState(false)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id })
  const rowDrop = useDroppable({ id })

  const style = {
    opacity: isDragging ? 0.5 : undefined,
  }

  const setRowRef = (node: HTMLTableRowElement | null) => {
    setNodeRef(node)
    rowDrop.setNodeRef(node)
  }

  return (
    <TableRow
      ref={setRowRef}
      style={style}
      className={clsx(
        'relative border-0 animate-in fade-in-0 slide-in-from-top-1 hover:bg-primary/5',
        activeDropPosition === 'inside' && 'bg-primary/10 ring-1 ring-inset ring-primary'
      )}
    >
      <TableCell className="w-12 border-0 py-0.5">
        {activeDropPosition === 'before' && <TableDropLine position="top" />}
        {activeDropPosition === 'after' && <TableDropLine position="bottom" />}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 cursor-grab bg-muted active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <IconDragHandleOutline className="size-4 text-muted-foreground" />
          <span className="sr-only">Drag menu item</span>
        </Button>
      </TableCell>
      <TableCell className="w-12 border-0 py-0.5">
        {showIcon ? (
          item.icon_name ? (
            <DynamicIcon
              iconName={item.icon_name}
              className="size-4"
              fallback={<IconHelpQuestionOutline className="size-5" />}
            />
          ) : (
            <IconHelpQuestionOutline className="size-5" />
          )
        ) : null}
      </TableCell>
      <TableCell className="border-0 py-0.5">
        <div className="flex items-center">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${paddingLeft}px` }}>
            {hasChildren ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto gap-1 p-0 font-medium hover:bg-transparent"
                onClick={() => toggleItem(id)}
              >
                <span className="font-bold">{item.name}</span>
                <IconRightChevronOutline
                  className={clsx('ml-2.5 size-2.5 shrink-0 transition-transform', { 'rotate-90': isExpanded })}
                />
              </Button>
            ) : (
              <span>{item.name}</span>
            )}
          </div>
          {activeDropPosition === 'inside' && (
            <span className="ml-3 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
              Drop as child
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="border-0 py-0.5">{item.page_id}</TableCell>
      <TableCell className="border-0 py-0.5">
        <div className="flex flex-wrap gap-1">
          {item.roles.map((role, i) => (
            <Badge key={i} variant="outline" className="bg-offwhite py-0">
              {role}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell className="border-0 py-0.5">
        <div className="flex items-center justify-end gap-x-2">
          <SheetWithFormComponent
            title={`Edit menu item: "${item.name}"`}
            triggerTooltip={`Edit menu item: "${item.name}"`}
            trigger={
              <Button variant="ghost" size="icon" className="size-6">
                <IconEditOutline size={14} />
              </Button>
            }
            isOpen={isEditItemSheetOpen}
            onOpenChange={setIsEditItemSheetOpen}
          >
            <MenuItemForm
              closeSheetOnSubmit
              onSubmit={(data) => {
                const formData = data as Record<string, unknown>
                const updatedItem: Partial<MessagesLandingMenuItem> = {
                  item_type: String(formData.item_type || ''),
                  name: String(formData.name || ''),
                  page_id: String(formData.page_id || ''),
                  description: String(formData.description || ''),
                  icon_name: formData.icon_name ? String(formData.icon_name) : undefined,
                  roles: (formData.assignedRoles as string[]) ?? item.roles,
                }
                editItem(id, updatedItem)
                setIsEditItemSheetOpen(false)
              }}
              initialValues={item}
              showIcon={showIcon}
            />
          </SheetWithFormComponent>
          <SheetWithFormComponent
            title={`Add child menu item to "${item.name}"`}
            triggerTooltip={`Add child menu item to "${item.name}"`}
            trigger={
              <Button variant="ghost" size="icon" className="size-6">
                <IconCirclePlusOutline size={14} />
              </Button>
            }
            isOpen={isAddChildItemSheetOpen}
            onOpenChange={setIsAddChildItemSheetOpen}
          >
            <MenuItemForm
              closeSheetOnSubmit
              showIcon={false}
              onSubmit={(data) => {
                const formData = data as Record<string, unknown>
                const menuItem: MessagesLandingMenuItem = {
                  item_type: String(formData.item_type || ''),
                  name: String(formData.name || ''),
                  page_id: String(formData.page_id || ''),
                  description: String(formData.description || ''),
                  icon_name: formData.icon_name ? String(formData.icon_name) : undefined,
                  roles: (formData.assignedRoles as string[]) ?? [],
                  items: [],
                }
                addChildItem(id, menuItem)
                setIsAddChildItemSheetOpen(false)
              }}
            />
          </SheetWithFormComponent>
          <TooltipWrapper tooltip="Delete Menu Item">
            <Button variant="ghost" size="icon" className="size-6" onClick={() => deleteItem(id)}>
              <IconDeleteOutline size={14} />
            </Button>
          </TooltipWrapper>
        </div>
      </TableCell>
    </TableRow>
  )
}

function TableDropLine({ position }: { position: 'top' | 'bottom' }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-primary',
        position === 'top' ? 'top-0' : 'bottom-0'
      )}
    />
  )
}
